import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { Modal } from "app/components/Modal";
import AddOrderField from "app/containers/CommonModal/AddOrderModal/AddOrderField";
import {
  OrderFieldConfig,
  useAddOrderModal,
} from "app/containers/CommonModal/AddOrderModal/useAddOrderModal";
import { addOrderModalSaga } from "app/containers/CommonModal/redux/saga";
import { reducer, sliceKey } from "app/containers/CommonModal/redux/slice";
import React from "react";
import { useInjectReducer, useInjectSaga } from "utils/redux-injectors";

import { useStyles } from "./styles";

type Props = {
  titleKey?: string;
};

const toNameOptions = (names: string[]) =>
  names
    .filter((name) => Boolean(name?.trim()))
    .map((name) => ({ value: name, label: name }));

const AddWorkOrderModal = ({
  titleKey = "WORK_ORDER.MODAL.ADD_TITLE",
}: Props) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: addOrderModalSaga });

  const styles = useStyles();
  const modal = useAddOrderModal();

  const renderField = (field: OrderFieldConfig) => (
    <AddOrderField
      key={field.key}
      fieldKey={field.key}
      label={field.label}
      type={field.type}
      options={field.options}
      inputMode={field.inputMode}
      disabled={field.disabled}
      dropDownKey={field.dropDownKey}
      value={modal.form[field.key]}
      fieldLabel={modal.fieldLabel}
      placeholder={modal.placeholder}
      optionLabel={modal.optionLabel}
      onChange={modal.onFieldChange}
      onDropDownScroll={modal.onDropDownScroll}
      hasMoreDropDownItems={modal.hasMoreDropDownItems}
    />
  );

  return (
    <Modal open={modal.open} onClose={modal.onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{modal.t(titleKey)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={modal.onClose}
            aria-label={modal.t("BUTTONS.CANCEL")}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {renderField({
            key: "customerName",
            label: "CUSTOMER_NAME",
            type: "select",
            dropDownKey: "customers",
            options: toNameOptions(modal.customerNames),
          })}
          {renderField({
            key: "subCustomer",
            label: "SUB_CUSTOMER",
            type: "select",
            options: modal.subCustomerOptions,
          })}
          {renderField({ key: "chassis", label: "CHASSIS", type: "text" })}
          {renderField({
            key: "manufacturer",
            label: "MANUFACTURER",
            type: "select",
            dropDownKey: "manufacturers",
            options: toNameOptions(modal.manufacturerNames),
          })}
          {renderField({
            key: "model",
            label: "MODEL",
            type: "select",
            dropDownKey: "models",
            options: toNameOptions(modal.modelNames),
          })}
          <Box className={styles.splitRow}>
            {modal.axleYearFields.map(renderField)}
          </Box>
          {renderField(modal.boxTypeField)}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={modal.onClose}
          >
            {modal.t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!modal.isValid}
            onClick={modal.onSubmit}
          >
            {modal.t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddWorkOrderModal;
